import { useDispatch, useSelector } from "react-redux";
import { BreadCrumbs, Button, ConfirmationDialog, Pagination, TableHeader, TableRow, TextInput, UserSideBar } from "../../components";
import { BreadCrumbsType } from "../../components/BreadCrumbs/BreadCrumbs";
import { AppDispatch, RootState } from "../../redux/store";
import { headersType, ToastVariants } from "../../models";
import { useContext, useEffect, useState } from "react";
import Calender from "../../assets/images/calender.svg";
import { searchAnnualPlanServices } from "../../redux/slices/ServiceSlice";
import { formatCurrency, getPlanDetails } from "../../utils";
import PlanSelection, { FamilyMemberStep, FamilyRow } from "./PlanSelection";
import SidebarFooter from "../../components/SidebarFooter";
import RadioInputNew from "../../components/RadioInput/RadioInputNew";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { PlanNames, SubscriptionType } from "../../models/annual-plan.model";
import { cancelAnnualPlan, getCardDetails, updateFamilyMembers } from "../../redux/slices/annualPlanSlice";
import { UserAnnualPlanState } from "../../models/user-annual-plan.model";
import moment from "moment";
import { SESSION_STORAGE_SHOW_ANNUAL_PLAN } from "../../utils/constants";
import UpdatePaymentMethod from "../../components/UpdatePaymentMethod";

const headers: headersType[] = [
  {
    title: "Service",
    extraClasses: "w-full pl-0",
    sortKey: "service",
    isClickable: false,
  },
  {
    title: "Service Fee",
    extraClasses: "w-full pl-0",
    sortKey: "fee",
    isClickable: false,
  },
  {
    title: "Annual Plan Discount",
    extraClasses: "w-full pl-0",
    sortKey: "discount",
    isClickable: false,
  },
];

const cancelReasons = [
  { label: "I am undergoing financial hardship", value: "I am undergoing financial hardship" },
  { label: "I no longer require services  included in the plan", value: "I no longer require services  included in the plan" },
  { label: "The annual plan does not meet my needs", value: "The annual plan does not meet my needs" },
  { label: "I have moved", value: "I have moved" },
  { label: "Other", value: "Other" },
];

const PatientPlans = () => {
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const [extraClasses] = useState(headers.map(head => head.extraClasses));
  const [showAnnualPlanOptions, setShowAnnualPlanOptions] = useState<"add" | "edit" | "">("");
  const [showUpdatePaymentOption, setShowUpdatePaymentOption] = useState(false);
  const [showUpdateFamilymembers, setShowUpdateFamilymembers] = useState(false);
  const [showCancelPlan, setShowCancelPlan] = useState(false);
  const [showCancelBlockedPopup, setShowCancelBlockedPopup] = useState(false);
  const [selectedCancelReason, setSelectedCancelReason] = useState("");
  const [selectedCancelReasonOther, setSelectedCancelReasonOther] = useState("");
  const [familyRows, setFamilyRows] = useState<FamilyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const { cardDetails } = useSelector((state: RootState) => state.annualPlan);
  const officeId = currentUser?.offices?.length ? currentUser?.offices[0]?.officeId : "";
  const { blockFeeServices, blockFeeServicesSortObj } = useSelector((state: RootState) => state.service);
  const selectedPlan = currentUser?.userAnnualPlan?.officeAnnualPlan ?? undefined;
  const breadcrumbs: BreadCrumbsType[] = [
    {
      title: "Annual Plan",
      key: "#",
    },
  ];

  const load = async (page = 0, size = 20, sortBy = "name,desc", term?: string) => {
    if (officeId)
      dispatch(
        searchAnnualPlanServices({
          page,
          size,
          sort: sortBy,
          term,
          officeId,
          enabledOnly: true,
        })
      );
  };

  const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    load(newPage, blockFeeServicesSortObj.rowsPerPage, blockFeeServicesSortObj.sortedBy, blockFeeServicesSortObj.searchTerm);
  };

  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    load(blockFeeServicesSortObj.pageNumber, parseInt(event.target.value, 10), blockFeeServicesSortObj.sortedBy, blockFeeServicesSortObj.searchTerm);
  };

  const renderCancelPlan = () => {
    return (
      <div className="flex flex-col gap-4">
        <h2 className="font-bold">Please select reason for cancelling Annual Plan</h2>
        <div className="flex flex-col">
          <RadioInputNew options={cancelReasons} label={""} value={selectedCancelReason} onChangeFunc={val => setSelectedCancelReason(val)} />
        </div>
        {selectedCancelReason === "Other" ? (
          <div>
            <TextInput
              label=""
              placeholder="Please enter the reason for your cancellation"
              value={selectedCancelReasonOther}
              onChangeFunc={e => setSelectedCancelReasonOther(e.target.value)}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const renderCancelBlockedContent = () => {
    return (
      <div className="flex flex-col gap-4">
        <h2 className="font-bold">You have signed up for a 12 month annual plan.</h2>
        <p>{`You can cancel following your last payment date of ${lastPaymentDate}`}</p>
      </div>
    );
  };

  const clear = () => {
    setShowCancelPlan(false);
    setSelectedCancelReason("");
    setSelectedCancelReasonOther("");
  };
  const handleCancelPlan = async () => {
    if (!selectedCancelReason) {
      toast?.errorToast("Please provide cancel reason.");
      return;
    }
    if (selectedCancelReason === "Other" && !selectedCancelReasonOther.trim()) {
      toast?.errorToast("Please provide other cancel reason.");
      return;
    }
    setLoading(true);
    if (officeId && currentUser?.userId && currentUser?.userAnnualPlan?.userAnnualPlanId) {
      const res = await dispatch(
        cancelAnnualPlan({
          officeId,
          userId: currentUser?.userId,
          userAnnualPlanId: currentUser?.userAnnualPlan?.userAnnualPlanId,
          reason: selectedCancelReason === "Other" ? selectedCancelReasonOther.trim() : selectedCancelReason,
        })
      );
      if (!res?.payload) toast?.errorToast("Annual plan cancellation failed.");
    }
    setLoading(false);
    clear();
  };

  const handleMembers = (firstName: string, lastName: string, dob: string) => {
    const isFamily = selectedPlan?.annualPlanType?.name.toLocaleLowerCase() === "family";
    const familyCount = isFamily ? 6 : 2;
    if (familyRows.length >= (isFamily ? familyCount : 1)) {
      toast?.openToast(`You can only have ${familyCount} family members with a ${selectedPlan?.annualPlanType?.name.toLocaleLowerCase()} plan.`);
      return;
    }
    setFamilyRows([...familyRows, { firstName, lastName, dob }]);
  };

  const removeMember = (index: number) => {
    const updatedRows = [...familyRows];
    updatedRows.splice(index, 1);
    setFamilyRows(updatedRows);
  };

  const setFamilyMember = () => {
    const familyMembers = currentUser?.userAnnualPlan?.additionalMembers
      ? currentUser?.userAnnualPlan?.additionalMembers.split("$").map(item => {
          const [firstName, lastName, dob] = item.split("|");
          return { firstName, lastName, dob };
        })
      : [];
    setFamilyRows(familyMembers);
  };

  const handleCancelFamily = () => {
    setFamilyMember();
    setShowUpdateFamilymembers(false);
  };

  const handleUpdateFamily = async () => {
    const family = familyRows?.length > 0 ? familyRows.map(item => `${item.firstName}|${item.lastName}|${item.dob}`).join("$") : undefined;
    if (!family) {
      toast?.errorToast("Please add atleast one family member.");
      return;
    }
    if (currentUser?.userAnnualPlan?.additionalMembers === family) {
      toast?.errorToast("Nothing to update.");
      return;
    }
    setLoading(true);
    if (officeId && currentUser?.userId && currentUser?.userAnnualPlan?.userAnnualPlanId && family?.length) {
      const res = await dispatch(
        updateFamilyMembers({ officeId, userId: currentUser?.userId, userAnnualPlanId: currentUser?.userAnnualPlan?.userAnnualPlanId, family })
      );

      if (res.payload) {
        toast?.openToast("Family updated successfully!", 2000, ToastVariants.Success);
      } else {
        toast?.errorToast("Family updated failed!");
      }
    }
    setLoading(false);
    setShowUpdateFamilymembers(false);
  };

  useEffect(() => {
    load();
  }, [currentUser?.offices]);

  useEffect(() => {
    if (currentUser?.userAnnualPlan && officeId) {
      dispatch(
        getCardDetails({
          officeId,
          userId: currentUser?.userId,
          userAnnualPlanId: currentUser?.userAnnualPlan?.userAnnualPlanId,
        })
      );
      setFamilyMember();
    }
  }, [currentUser?.userAnnualPlan, officeId]);

  const planDetails = getPlanDetails(currentUser?.userAnnualPlan?.officeAnnualPlan);

  const nextPaymentDate = moment(currentUser?.userAnnualPlan?.renewalDate).format("ll");
  const lastPaymentDate = moment(currentUser?.userAnnualPlan?.expirationDate).subtract(1, "M").format("ll");

  useEffect(() => {
    const ShowAnnualPlanSidebar = sessionStorage.getItem(SESSION_STORAGE_SHOW_ANNUAL_PLAN);
    if (ShowAnnualPlanSidebar === "true") {
      setShowAnnualPlanOptions("add");
      sessionStorage.removeItem(SESSION_STORAGE_SHOW_ANNUAL_PLAN);
    }
  }, []);

  const validOfficeID = ["f55aa392-0b05-4d37-9836-90f4d2045eba", "be61f117-1fdf-4985-85c1-d5c5ffe98f41", "497ceebd-9585-46bb-9e54-898dee39004f"];
  const showEditButton = currentUser?.offices?.length && validOfficeID.includes(currentUser?.offices[0]?.officeId ?? "");

  return (
    <>
      <main>
        <div className="sm:container sm:mx-auto p-8 min-h-[80vh] flex">
          <div className="w-full md:flex gap-10">
            <div className="w-full md:w-3/5 mb-5">
              <BreadCrumbs items={breadcrumbs} />
              <div className="md:flex justify-between items-center mb-4 md:mb-6">
                <h2 className="heading uppercase">Hi {currentUser?.firstName}</h2>
              </div>
              <p className="text-base mb-2 md:mb-5 font-medium">You will find a list of all annual plan services below.</p>
              <div className="block">
                <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
                  {headers.map((head, index) => (
                    <TableHeader
                      title={head.title}
                      index={index}
                      key={index}
                      sortIcon={null}
                      handleSort={() => null}
                      extraClassesHeader={extraClasses[index]}
                      isClickable={head.isClickable}
                    />
                  ))}
                </div>
                {blockFeeServices.map((rowData, index: number) => {
                  const sortedData = {
                    name: rowData.name ?? "-",
                    price: formatCurrency((rowData.price / 100).toString(), "blur") ?? "-",
                    discount: rowData.annualPlanDiscountPct ? `${rowData.annualPlanDiscountPct}%` : "-",
                  };
                  return (
                    <TableRow
                      handleCloseTooltip={() => {}}
                      handleOpenTooltip={() => {}}
                      onRowClick={() => {}}
                      tooltipVisible={false}
                      data={Object.values(sortedData)}
                      key={index}
                      id={index.toString()}
                      extraClassesRow={extraClasses}
                    />
                  );
                })}
                {blockFeeServices?.length ? (
                  <Pagination
                    colSpan={10}
                    count={blockFeeServicesSortObj.totalElements ?? 0}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                    page={blockFeeServicesSortObj.pageNumber}
                    rowsPerPage={blockFeeServicesSortObj.rowsPerPage}
                  />
                ) : null}
              </div>
            </div>
            <div className="w-full md:w-2/5">
              {!planDetails ? (
                <div className="flex flex-col gap-4 bg-docnote-grey rounded-3xl p-10 mb-5">
                  {currentUser?.userAnnualPlanDate ? (
                    <h2 className="text-docsigna-purple font-bold">You have been associated to a couples or family plan.</h2>
                  ) : (
                    <div>
                      <h2 className="uppercase text-docsigna-purple font-bold">Your annual Plan details</h2>
                      {/* {currentUser?.offices?.length && <p>{currentUser?.offices[0]?.annualPlanDescription}</p>} */}
                      <p className="mb-2">{`You are not currently enrolled in your office's annual plan. Join now to take advantage of the benefits and savings.`}</p>
                      <Button text="Get Started" width="fit" onClick={() => setShowAnnualPlanOptions("add")} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4 bg-docnote-grey rounded-3xl p-10">
                  <div className="px-4">
                    <h2 className="uppercase text-docsigna-purple font-bold">You are currently enrolled in the following Annual Plan:</h2>
                    <p>
                      {planDetails?.planName} {planDetails?.price}/{planDetails?.subscriptionType}
                    </p>
                    {currentUser?.userAnnualPlan?.isEditable && showEditButton && (
                      <div className="w-fit mt-1">
                        <Button text="Edit" onClick={() => setShowAnnualPlanOptions("add")} />
                      </div>
                    )}
                  </div>
                  {![PlanNames.Single, PlanNames.Senior].includes(planDetails.planName) && familyRows?.length ? (
                    <div className="border p-4">
                      <h2 className="uppercase text-docsigna-purple font-bold">Family members included under the annual plan</h2>
                      {familyRows.length ? (
                        <div className="flex flex-wrap gap-6">
                          {familyRows.map((row, index) => (
                            <div key={index} className="flex flex-wrap items-center gap-2">
                              <div className="flex flex-wrap items-center gap-3">
                                <p>Name</p>
                                <p className="font-bold capitalize">
                                  {row.firstName} {row.lastName}
                                </p>
                              </div>
                              <div className="flex flex-wrap items-center gap-3">
                                <p>Date of Birth</p>
                                <p className="font-bold">{moment(row.dob).format("ll")}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      <div className="flex justify-end">
                        <p className="text-docnote-orange cursor-pointer text-xs" onClick={() => setShowUpdateFamilymembers(true)}>
                          Edit
                        </p>
                      </div>
                    </div>
                  ) : null}
                  <div className="flex flex-col gap-5 border p-4">
                    <div>
                      <h2 className="uppercase text-docsigna-purple font-bold">Payment Information:</h2>
                      <div className="flex flex-wrap justify-between">
                        <div className="flex flex-col gap-5">
                          <p>Credit Card Number</p>
                          <p>**** **** **** {cardDetails?.card4Digits}</p>
                        </div>
                        <div className="flex flex-col gap-5">
                          <p>Expiry Date</p>
                          <p>{cardDetails?.cardExpirationDate}</p>
                        </div>
                      </div>
                      <Button text="Update Credit Card" width="fit" className="mt-5" onClick={() => setShowUpdatePaymentOption(true)} />
                    </div>
                    {planDetails?.subscriptionType === SubscriptionType.Monthly && nextPaymentDate ? (
                      <div>
                        <h2 className="uppercase text-docsigna-purple font-bold">Next Payment Date:</h2>
                        <p>{nextPaymentDate}</p>
                      </div>
                    ) : null}
                    {currentUser?.userAnnualPlan?.userAnnualPlanState !== UserAnnualPlanState.Active &&
                      planDetails?.subscriptionType === SubscriptionType.Monthly && (
                        <>
                          {lastPaymentDate && (
                            <div>
                              <h2 className="uppercase text-docsigna-purple font-bold">Last Payment Date:</h2>
                              <p>{moment(lastPaymentDate).format("ll")}</p>
                            </div>
                          )}
                        </>
                      )}
                  </div>
                  <div className="pt-2 pb-6">
                    <div className={`flex justify-end items-center`}>
                      {/* FIXME: Add back in once backend updates are finished */}
                      {/* {currentUser?.userAnnualPlan?.userAnnualPlanState === UserAnnualPlanState.Active &&
                      planDetails?.subscriptionType === SubscriptionType.Monthly ? (
                        <Button
                          text="Update Annual Plan"
                          width="fit"
                          className="mb-5"
                          onClick={() => setShowAnnualPlanOptions("edit")}
                        />
                      ) : null} */}
                      {currentUser?.userAnnualPlan?.userAnnualPlanState === UserAnnualPlanState.Active ? (
                        <p
                          className="text-docnote-orange cursor-pointer text-xs"
                          onClick={() => {
                            if (
                              currentUser?.userAnnualPlan?.officeAnnualPlan.annualPlanType.subscriptionType === "monthly" &&
                              moment().isBefore(lastPaymentDate)
                            )
                              setShowCancelBlockedPopup(true);
                            else setShowCancelPlan(true);
                          }}
                        >
                          Cancel Plan
                        </p>
                      ) : (
                        <p className="text-docnote-orange cursor-pointer text-xs">Cancelled</p>
                      )}
                    </div>

                    {currentUser?.userAnnualPlan?.userAnnualPlanState !== UserAnnualPlanState.Active ? (
                      <div className="pl-4">
                        <h2 className="uppercase text-docsigna-purple font-bold">Valid Until</h2>
                        <div className="flex gap-5">
                          <p>{moment(currentUser?.userAnnualPlan?.expirationDate).format("ll")}</p>{" "}
                          <div className="w-6 h-6 rounded-full bg-docnote-orange flex items-center justify-center">
                            <img src={Calender} alt="calender" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="pl-4">
                        <h2 className="uppercase text-docsigna-purple font-bold">Renewal Date</h2>
                        <div className="flex gap-5">
                          <p>{moment(currentUser?.userAnnualPlan?.expirationDate).format("ll")}</p>{" "}
                          <div className="w-6 h-6 rounded-full bg-docnote-orange flex items-center justify-center">
                            <img src={Calender} alt="calender" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <UserSideBar
        visible={showAnnualPlanOptions !== ""}
        LeftbuttonText=""
        RightbuttonText=""
        onCancelClick={undefined}
        onInviteClick={undefined}
        onClickOutside={() => null}
        title="ANNUAL PLAN OPTIONS"
      >
        <PlanSelection
          officeId={officeId ?? ""}
          currentPlan={currentUser?.userAnnualPlan}
          handleClose={() => setShowAnnualPlanOptions("")}
          isEdit={showAnnualPlanOptions === "edit"}
        />
      </UserSideBar>
      <UserSideBar
        visible={showUpdatePaymentOption}
        LeftbuttonText="Cancel"
        RightbuttonText="Update"
        onCancelClick={() => setShowUpdatePaymentOption(false)}
        onInviteClick={() => setShowUpdatePaymentOption(false)}
        onClickOutside={() => null}
        title="Update Payment Method"
      >
        {officeId && currentUser?.userId && currentUser?.userAnnualPlan?.userAnnualPlanId ? (
          <UpdatePaymentMethod
            officeId={officeId}
            userId={currentUser?.userId}
            userAnnualPlanId={currentUser?.userAnnualPlan?.userAnnualPlanId}
            handleCancel={() => setShowUpdatePaymentOption(false)}
          />
        ) : (
          <></>
        )}
      </UserSideBar>
      <UserSideBar
        visible={showUpdateFamilymembers}
        LeftbuttonText="Cancel"
        RightbuttonText="Update"
        onCancelClick={() => setShowUpdateFamilymembers(false)}
        onInviteClick={() => setShowUpdateFamilymembers(false)}
        onClickOutside={() => null}
        title="Update Family Members"
      >
        <div className="px-8 relative">
          <FamilyMemberStep familyRows={familyRows} addMember={handleMembers} removeMember={removeMember} />
          <SidebarFooter
            cancelText={"Cancel"}
            saveText={"Update"}
            isDisabledNext={false}
            loading={loading}
            handleClose={() => handleCancelFamily()}
            handleSave={() => handleUpdateFamily()}
          />
        </div>
      </UserSideBar>
      <ConfirmationDialog
        open={showCancelPlan}
        description={renderCancelPlan()}
        title={""}
        loading={loading}
        successButtonText={"Submit"}
        handleSuccess={handleCancelPlan}
        failureButtonText={"Cancel"}
        handleFailure={clear}
      />
      <ConfirmationDialog
        open={showCancelBlockedPopup}
        description={renderCancelBlockedContent()}
        title={""}
        loading={loading}
        successButtonText={"Okay"}
        handleSuccess={() => setShowCancelBlockedPopup(false)}
        failureButtonText={""}
        handleFailure={() => {}}
      />
    </>
  );
};

export default PatientPlans;
